import { useLantern, useSession } from "../hooks";
import { t, tc } from "../i18n";

type Obj = Record<string, unknown>;

const triggerTypes = (): [string, string][] => [
  ["manual", t("Run by hand")],
  ["shortcut", t("Shortcut on messages")],
  ["command", t("Slash command")],
  ["message", t("Message in a channel")],
  ["reaction", t("Reaction in a channel")],
  ["schedule", tc("trigger", "Schedule")],
  ["webhook", t("Webhook")],
  ["task", t("Task reaches a state")],
];

const stepTypes = (): [string, string][] => [
  ["message", t("Post a message")],
  ["approval", t("Ask for approval")],
  ["form", t("Ask someone to fill in a form")],
  ["task", t("Hand work to an agent")],
  ["http", t("Call a URL")],
  ["mcp", t("Call an MCP tool")],
  ["branch", t("Branch")],
  ["create_channel", t("Create a channel")],
  ["invite", t("Invite to a channel")],
];

const taskStates = (): [string, string][] => [
  ["completed", t("completed")],
  ["failed", t("failed")],
  ["cancelled", t("cancelled")],
  ["dead_lettered", t("dead lettered")],
  ["input_required", t("input required")],
];

// WorkflowForm edits a workflow definition (docs/specs/workflows.md) as a
// form, for sites with no model to write one from a description. Fields
// that hold JSON are edited as JSON; text fields take {{templates}}.
export function WorkflowForm({ value, onChange }: { value: Obj; onChange: (v: Obj) => void }) {
  const session = useSession();
  const state = useLantern();
  const channels = [...state.channels.values()]
    .map((c) => c.channel)
    .filter((c) => c.kind === "public" || c.kind === "private")
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  const people = [...session.directory.values()].filter((p) => p.kind === "user").sort((a, b) => a.handle.localeCompare(b.handle));
  const trigger = (value.trigger as Obj) ?? { type: "manual" };
  const steps = (value.steps as Obj[]) ?? [];
  const ids = steps.map((s) => String(s.id ?? ""));

  const set = (k: string, v: unknown) => onChange(clean({ ...value, [k]: v }));
  const setTrigger = (k: string, v: unknown) => set("trigger", clean({ ...trigger, [k]: v }));
  const setStep = (i: number, s: Obj) => set("steps", steps.map((x, j) => (j === i ? clean(s) : x)));
  const move = (i: number, by: number) => {
    const next = [...steps];
    const [s] = next.splice(i, 1);
    next.splice(i + by, 0, s);
    set("steps", next);
  };
  const add = (type: string) => {
    let n = steps.length + 1;
    while (ids.includes(`${type}_${n}`)) n++;
    set("steps", [...steps, { id: `${type}_${n}`, type }]);
  };

  const channelSelect = (v: unknown, change: (v: string) => void, label: string, allowTemplate = false) => (
    <label>
      {label}
      <select value={String(v ?? "")} onChange={(e) => change(e.target.value)}>
        <option value="">—</option>
        {allowTemplate && <option value="{{trigger.channel_id}}">{t("The channel it started in")}</option>}
        {channels.map((c) => (
          <option key={c.id} value={c.id}>
            #{c.name}
          </option>
        ))}
        {typeof v === "string" && v && !channels.some((c) => c.id === v) && v !== "{{trigger.channel_id}}" && <option value={v}>{v}</option>}
      </select>
    </label>
  );

  return (
    <div className="workflow-form">
      <fieldset>
        <legend>{t("Workflow")}</legend>
        <label>
          {t("Name")}
          <input value={String(value.name ?? "")} onChange={(e) => set("name", e.target.value)} placeholder="deploy-approval" />
        </label>
        <label>
          {t("Description")}
          <input value={String(value.description ?? "")} onChange={(e) => set("description", e.target.value)} />
        </label>
        <label className="check">
          <input type="checkbox" checked={value.enabled !== false} onChange={(e) => set("enabled", e.target.checked ? undefined : false)} />
          {tc("enabled", "On")}
        </label>
      </fieldset>

      <fieldset>
        <legend>{tc("trigger", "When")}</legend>
        <label>
          {t("Trigger")}
          <select value={String(trigger.type)} onChange={(e) => set("trigger", { type: e.target.value })}>
            {triggerTypes().map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
        {trigger.type === "shortcut" && (
          <>
            <Text label={t("Label in the menu")} v={trigger.label} change={(v) => setTrigger("label", v)} />
            {channelSelect(trigger.channel_id, (v) => setTrigger("channel_id", v), t("Only in channel"))}
          </>
        )}
        {trigger.type === "command" && <Text label={t("Command")} v={trigger.command} change={(v) => setTrigger("command", v)} placeholder="/standup" />}
        {(trigger.type === "message" || trigger.type === "reaction") && channelSelect(trigger.channel_id, (v) => setTrigger("channel_id", v), t("Channel"))}
        {trigger.type === "message" && (
          <>
            <Text label={t("Pattern (regular expression)")} v={trigger.pattern} change={(v) => setTrigger("pattern", v)} placeholder="^deploy (\w+)" />
            <label className="check">
              <input type="checkbox" checked={!!trigger.include_agents} onChange={(e) => setTrigger("include_agents", e.target.checked || undefined)} />
              {t("Agents' messages too")}
            </label>
          </>
        )}
        {trigger.type === "reaction" && <Text label={t("Emoji")} v={trigger.emoji} change={(v) => setTrigger("emoji", v)} placeholder="ticket" />}
        {trigger.type === "schedule" && (
          <>
            <Text label={t("Cron")} v={trigger.cron} change={(v) => setTrigger("cron", v)} placeholder="0 9 * * 1-5" />
            <Text label={t("Time zone")} v={trigger.timezone} change={(v) => setTrigger("timezone", v)} placeholder="Europe/Berlin" />
          </>
        )}
        {trigger.type === "task" && (
          <>
            <Text label={t("Capability")} v={trigger.capability} change={(v) => setTrigger("capability", v)} placeholder="deploy.run/v1" />
            <div className="states">
              {taskStates().map(([st, l]) => {
                const on = ((trigger.states as string[]) ?? []).includes(st);
                return (
                  <label key={st} className="check">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => {
                        const cur = (trigger.states as string[]) ?? [];
                        setTrigger("states", on ? cur.filter((x) => x !== st) : [...cur, st]);
                      }}
                    />
                    {l}
                  </label>
                );
              })}
            </div>
          </>
        )}
      </fieldset>

      <fieldset>
        <legend>{t("Steps")}</legend>
        <ol className="steps">
          {steps.map((s, i) => {
            const put = (k: string, v: unknown) => setStep(i, { ...s, [k]: v });
            return (
              <li key={i} className="step">
                <div className="step-head">
                  <Text label={t("Step id")} v={s.id} change={(v) => put("id", v)} />
                  <select aria-label={t("Step type")} value={String(s.type)} onChange={(e) => setStep(i, { id: s.id, type: e.target.value })}>
                    {stepTypes().map(([k, l]) => (
                      <option key={k} value={k}>
                        {l}
                      </option>
                    ))}
                  </select>
                  <button className="secondary" disabled={i === 0} onClick={() => move(i, -1)} aria-label={t("Move up")}>
                    ↑
                  </button>
                  <button className="secondary" disabled={i === steps.length - 1} onClick={() => move(i, 1)} aria-label={t("Move down")}>
                    ↓
                  </button>
                  <button className="secondary" onClick={() => set("steps", steps.filter((_, j) => j !== i))} aria-label={t("Remove step")}>
                    ✕
                  </button>
                </div>
                {s.type === "message" && (
                  <>
                    {channelSelect(s.channel_id, (v) => put("channel_id", v), t("Channel"), true)}
                    <Text label={t("Text")} v={s.text} change={(v) => put("text", v)} area />
                    <Text label={t("In thread of (message id or template)")} v={s.thread_root_id} change={(v) => put("thread_root_id", v)} />
                  </>
                )}
                {s.type === "approval" && (
                  <>
                    <label>
                      {t("Approvers")}
                      <select
                        multiple
                        value={(s.approvers as string[]) ?? []}
                        onChange={(e) => put("approvers", [...e.target.selectedOptions].map((o) => o.value))}
                      >
                        {people.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.display_name || p.handle}
                          </option>
                        ))}
                      </select>
                    </label>
                    <Text label={t("Question")} v={s.prompt} change={(v) => put("prompt", v)} area />
                  </>
                )}
                {s.type === "form" && (
                  <>
                    <label>
                      {tc("form", "Ask")}
                      <select value={String(s.to ?? "")} onChange={(e) => put("to", e.target.value)}>
                        <option value="">—</option>
                        <option value="{{trigger.user_id}}">{t("Whoever started it")}</option>
                        {people.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.display_name || p.handle}
                          </option>
                        ))}
                      </select>
                    </label>
                    <Text label={t("Question")} v={s.prompt} change={(v) => put("prompt", v)} area />
                    <Json label={t("Form (JSON Schema)")} v={s.schema} change={(v) => put("schema", v)} />
                  </>
                )}
                {s.type === "task" && (
                  <>
                    <Text label={t("Capability")} v={s.capability} change={(v) => put("capability", v)} placeholder="summary/v1" />
                    <Json label={t("Input")} v={s.input} change={(v) => put("input", v)} />
                    <label>
                      {t("Priority")}
                      <select value={String(s.priority ?? "")} onChange={(e) => put("priority", e.target.value)}>
                        <option value="">{t("normal")}</option>
                        <option value="interactive">{t("interactive")}</option>
                        <option value="urgent">{t("urgent")}</option>
                        <option value="background">{t("background")}</option>
                      </select>
                    </label>
                  </>
                )}
                {s.type === "http" && (
                  <>
                    <label>
                      {t("Method")}
                      <select value={String(s.method ?? "POST")} onChange={(e) => put("method", e.target.value)}>
                        {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                          <option key={m}>{m}</option>
                        ))}
                      </select>
                    </label>
                    <Text label={t("URL (on the workspace's allowlist)")} v={s.url} change={(v) => put("url", v)} />
                    <Json label={t("Body")} v={s.body} change={(v) => put("body", v)} />
                  </>
                )}
                {s.type === "mcp" && (
                  <>
                    <Text label={t("Server")} v={s.server} change={(v) => put("server", v)} />
                    <Text label={t("Tool")} v={s.tool} change={(v) => put("tool", v)} />
                    <Json label={t("Arguments")} v={s.arguments} change={(v) => put("arguments", v)} />
                  </>
                )}
                {s.type === "branch" && <Json label={t("If (condition)")} v={s.if} change={(v) => put("if", v)} placeholder='{"path": "steps.ask.output.approved", "equals": true}' />}
                {s.type === "create_channel" && (
                  <>
                    <Text label={t("Name")} v={s.name} change={(v) => put("name", v)} />
                    <Text label={t("Topic")} v={s.topic} change={(v) => put("topic", v)} />
                  </>
                )}
                {s.type === "invite" && channelSelect(s.channel_id, (v) => put("channel_id", v), t("Channel"), true)}
                <div className="step-flow">
                  {(s.type === "approval" || s.type === "form" || s.type === "task") && (
                    <Text label={t("Give up after")} v={s.timeout} change={(v) => put("timeout", v)} placeholder="24h" />
                  )}
                  {s.type === "branch" ? (
                    <Next label={t("If it does not hold, go to")} v={s.else} ids={ids} self={String(s.id)} change={(v) => put("else", v)} end />
                  ) : (
                    <Next label={tc("step", "Then")} v={s.then} ids={ids} self={String(s.id)} change={(v) => put("then", v)} />
                  )}
                </div>
              </li>
            );
          })}
        </ol>
        <label className="add-step">
          {t("Add a step")}
          <select value="" onChange={(e) => e.target.value && add(e.target.value)}>
            <option value="">…</option>
            {stepTypes().map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
      </fieldset>
    </div>
  );
}

// clean drops empty fields, so the definition stays what one would write.
function clean(o: Obj): Obj {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== "" && v !== undefined && !(Array.isArray(v) && v.length === 0)));
}

function Text({ label, v, change, area, placeholder }: { label: string; v: unknown; change: (v: string) => void; area?: boolean; placeholder?: string }) {
  return (
    <label>
      {label}
      {area ? (
        <textarea value={String(v ?? "")} onChange={(e) => change(e.target.value)} rows={2} placeholder={placeholder} />
      ) : (
        <input value={String(v ?? "")} onChange={(e) => change(e.target.value)} placeholder={placeholder} />
      )}
    </label>
  );
}

// Json edits a JSON-valued field, keeping the text while it does not parse.
function Json({ label, v, change, placeholder }: { label: string; v: unknown; change: (v: unknown) => void; placeholder?: string }) {
  const text = v === undefined ? "" : typeof v === "string" && !looksJSON(v) ? v : JSON.stringify(v, null, 1);
  return (
    <label>
      {label}
      <textarea
        key={text}
        className="json"
        defaultValue={text}
        rows={3}
        placeholder={placeholder}
        spellCheck={false}
        onBlur={(e) => {
          const raw = e.target.value.trim();
          if (!raw) return change(undefined);
          try {
            change(JSON.parse(raw));
          } catch {
            change(raw); // a template or plain text; saving checks it
          }
        }}
      />
    </label>
  );
}

function looksJSON(s: string) {
  return /^\s*[[{]/.test(s);
}

function Next({ label, v, ids, self, change, end }: { label: string; v: unknown; ids: string[]; self: string; change: (v: string) => void; end?: boolean }) {
  return (
    <label>
      {label}
      <select value={String(v ?? "")} onChange={(e) => change(e.target.value)}>
        <option value="">{end ? t("end") : t("the next step")}</option>
        {!end && <option value="end">{t("end")}</option>}
        {ids
          .filter((id) => id && id !== self)
          .map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
      </select>
    </label>
  );
}
